import { Injectable } from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { GoApplicationPurposeCodeEnum } from '../../constants/go-application-purpose-code-enum';
import { GoApplicationRequestFormModel } from '../../models/go-application-request-form-model';
import { GoApplicationFormTypeControlsMap } from './types/go-application-form-type-controls-map';
import { GoApplicationFormControlValidatorsMap } from './types/go-application-form-control-validators-map';
import { PurposeOfFilingValidatorConfig } from '../../../../shared/constants/purpose-of-filing-validation-configurations';
import { GoLocationType } from '../../../go-parameters/models/go-location-type';
import { GoFrequencyType } from '../../../go-parameters/models/go-frequency-type';
import { GoDataDirectionType } from '../../../go-parameters/models/go-data-direction-type';
import { GoStationClassType } from '../../../go-parameters/models/go-station-class-type';
import { GoParametersValidationMessages } from '../../../go-parameters/constants/go-parameters-validation-messages';
import { GoCoordinatesType } from '../../../go-parameters/models/go-coordinates-type';
import {
    LatitudeControlValidators,
    LongitudeControlValidators,
} from '../../../../shared/components/latitude-longitude-form/constants/latitude-longitude-control-validators';
import { GoAntennaHeightUnitType } from '../../../go-parameters/models/go-antenna-height-unit-type';
import { ErpControlValidators } from '../../../../shared/constants/frequency-control-validators';
import { GoEmissionDesignatorPowerType } from '../../../go-parameters/models/go-emission-designator-power-type';
import { GoEmissionDesignatorInformationType } from '../../../go-parameters/models/go-emission-designator-information-type';
import { GoApplicationFormControlDefaultValuesMap } from './types/go-application-form-control-default-values-map';
import {
    isFalsyOrWhitespace,
    tryParseToNumber,
} from '../../../../../../core/functions/string-helpers';
import { GoApplicationRequest } from '../../models/go-application-request';

@Injectable({
    providedIn: 'root',
})
export class GoApplicationPreparationService {
    /**
     * The form controls that are common to all types of go applications.
     */
    readonly commonControlsForAllGoApplicationTypes: (keyof GoApplicationRequestFormModel)[] =
        [
            'applicationPurpose',
            'frequencyType',
            'locationType',
            'stationClassType',
            'dataDirectionType',
            'numberOfFrequencies',
            'coordinatesType',
            'latitude',
            'longitude',
            'coordinatesType',
            'antennaHeightUnitType',
            'heightToTip',
            'fixedErp',
            'mobileErp',
            'emissionDesignatorPowerType',
            'emissionDesignatorInformationType',
        ];

    /**
     * The default values for the form controls.
     */
    readonly controlDefaultValues: GoApplicationFormControlDefaultValuesMap = {
        applicationPurpose: null,
        fccRegistrationNumber: null,
        callSign: null,
        frequencyType: null,
        locationType: null,
        stationClassType: null,
        dataDirectionType: null,
        numberOfFrequencies: null,
        coordinatesType: null,
        latitude: null,
        longitude: null,
        towerRegistrationNumber: null,
        antennaHeightUnitType: GoAntennaHeightUnitType.Meters,
        heightToTip: null,
        fixedErp: '40',
        mobileErp: '5',
        emissionDesignatorPowerType: null,
        emissionDesignatorInformationType: null,
    };

    /**
     * The mapping of go controls to their respective validators.
     */
    readonly controlValidators: GoApplicationFormControlValidatorsMap = {
        applicationPurpose: [Validators.required],
        fccRegistrationNumber: [],
        callSign: [
            ...PurposeOfFilingValidatorConfig.callSign.default,
            ...PurposeOfFilingValidatorConfig.callSign.required,
        ],
        frequencyType: [Validators.required],
        locationType: [Validators.required],
        stationClassType: [
            Validators.required,
            this.stationClassTypeControlValidator(),
        ],
        dataDirectionType: [this.dataDirectionTypeControlRequiredValidator()],
        numberOfFrequencies: [Validators.required],
        coordinatesType: [
            Validators.required,
            this.toggleAsrControlValidator(),
        ],
        latitude: LatitudeControlValidators,
        longitude: LongitudeControlValidators,
        towerRegistrationNumber: [],
        antennaHeightUnitType: [this.heightToTipControlRequiredValidator()],
        heightToTip: [this.heightToTipControlRequiredValidator()],
        fixedErp: [
            ...ErpControlValidators,
            this.fixedErpControlRequiredValidator(),
        ],
        mobileErp: [...ErpControlValidators, Validators.required],
        emissionDesignatorPowerType: [Validators.required],
        emissionDesignatorInformationType: [Validators.required],
    };

    private applicationPreparationForm: FormGroup<
        FormGroupType<GoApplicationRequestFormModel>
    > = this.getFormDefinition();

    getFormDefinition(): FormGroup<
        FormGroupType<GoApplicationRequestFormModel>
    > {
        return new FormGroup<FormGroupType<GoApplicationRequestFormModel>>({
            applicationPurpose:
                new FormControl<GoApplicationPurposeCodeEnum | null>(
                    this.controlDefaultValues.applicationPurpose,
                    this.controlValidators.applicationPurpose,
                ),
            callSign: new FormControl<string | null>(
                this.controlDefaultValues.callSign,
                this.controlValidators.callSign,
            ),
            fccRegistrationNumber: new FormControl<string | null>(
                this.controlDefaultValues.fccRegistrationNumber,
                this.controlValidators.fccRegistrationNumber,
            ),
            frequencyType: new FormControl<GoFrequencyType | null>(
                this.controlDefaultValues.frequencyType,
                this.controlValidators.frequencyType,
            ),
            locationType: new FormControl<GoLocationType | null>(
                this.controlDefaultValues.locationType,
                this.controlValidators.locationType,
            ),
            stationClassType: new FormControl<GoStationClassType | null>(
                this.controlDefaultValues.stationClassType,
                this.controlValidators.stationClassType,
            ),
            dataDirectionType: new FormControl<GoDataDirectionType | null>(
                this.controlDefaultValues.dataDirectionType,
                this.controlValidators.dataDirectionType,
            ),
            numberOfFrequencies: new FormControl<number | null>(
                this.controlDefaultValues.numberOfFrequencies,
                this.controlValidators.numberOfFrequencies,
            ),
            coordinatesType: new FormControl<GoCoordinatesType | null>(
                this.controlDefaultValues.coordinatesType,
                this.controlValidators.coordinatesType,
            ),
            latitude: new FormControl<string | null>(
                this.controlDefaultValues.latitude,
                this.controlValidators.latitude,
            ),
            longitude: new FormControl<string | null>(
                this.controlDefaultValues.longitude,
                this.controlValidators.longitude,
            ),
            towerRegistrationNumber: new FormControl<string | null>(
                this.controlDefaultValues.towerRegistrationNumber,
                this.controlValidators.towerRegistrationNumber,
            ),
            antennaHeightUnitType:
                new FormControl<GoAntennaHeightUnitType | null>(
                    this.controlDefaultValues.antennaHeightUnitType,
                    this.controlValidators.antennaHeightUnitType,
                ),
            heightToTip: new FormControl<string | null>(
                this.controlDefaultValues.heightToTip,
                this.controlValidators.heightToTip,
            ),
            fixedErp: new FormControl<string | null>(
                this.controlDefaultValues.fixedErp,
                this.controlValidators.fixedErp,
            ),
            mobileErp: new FormControl<string | null>(
                this.controlDefaultValues.mobileErp,
                this.controlValidators.mobileErp,
            ),
            emissionDesignatorPowerType: new FormControl<
                GoEmissionDesignatorPowerType[] | null
            >(
                this.controlDefaultValues.emissionDesignatorPowerType,
                this.controlValidators.emissionDesignatorPowerType,
            ),
            emissionDesignatorInformationType:
                new FormControl<GoEmissionDesignatorInformationType | null>(
                    this.controlDefaultValues.emissionDesignatorInformationType,
                    this.controlValidators.emissionDesignatorInformationType,
                ),
        });
    }

    /**
     * The mapping of go application types to their respective form controls with validators.
     */
    readonly goApplicationTypeToControlsMapping: GoApplicationFormTypeControlsMap<GoApplicationPurposeCodeEnum> =
        {
            [GoApplicationPurposeCodeEnum.NewLicense]: [
                ...this.commonControlsForAllGoApplicationTypes,
                'fccRegistrationNumber',
            ],
            [GoApplicationPurposeCodeEnum.NewLocation]: [
                ...this.commonControlsForAllGoApplicationTypes,
                'callSign',
            ],
            [GoApplicationPurposeCodeEnum.NewFrequencies]: [
                ...this.commonControlsForAllGoApplicationTypes,
                'callSign',
            ],
        };

    constructor() {}

    /**
     * Resets the form to its initial state.
     */
    resetForm(): void {
        // TODO: when form state is saved in cache, clear cache here.
        this.applicationPreparationForm.reset();
    }

    /**
     * Copies the disabled state of form controls from one form to another.
     * @param form The source form group.
     * @param formToUpdate The target form group to update.
     */
    copyFormControlDisabledState<TFormModel>(
        form: FormGroup<FormGroupType<TFormModel>>,
        formToUpdate: FormGroup<FormGroupType<GoApplicationRequestFormModel>>,
    ): void {
        Object.entries(form.controls).forEach(([key, control]) => {
            if (control.disabled === true) {
                const formControl =
                    formToUpdate.controls[
                        key as keyof GoApplicationRequestFormModel
                    ];

                formControl.setValue(null);
                formControl.disable({ emitEvent: false });
            }
        });
    }

    /**
     * Updates the section control sections based on the current form values.
     * @param form The form group.
     */
    updateSectionControls(
        form: FormGroup<FormGroupType<GoApplicationRequestFormModel>>,
    ): void {
        this.updateStationClassSectionControls(
            form.controls.locationType,
            form.controls.stationClassType,
        );
        this.updateAntennaHeightSectionControls(
            form.controls.locationType,
            form.controls.antennaHeightUnitType,
            form.controls.heightToTip,
        );
        this.updateMinimumErpSectionControls(
            form.controls.locationType,
            form.controls.fixedErp,
        );
        this.updateDataDirectionTypeSectionControls(
            form.controls.locationType,
            form.controls.stationClassType,
            form.controls.dataDirectionType,
        );
    }

    updateGoApplicationFormControls(
        form: FormGroup<FormGroupType<GoApplicationRequestFormModel>>,
    ): void {
        const goApplicationType = form.controls.applicationPurpose.value;

        if (!goApplicationType) {
            return;
        }

        Object.entries(form.controls).forEach(([key, control]) => {
            const validators =
                this.controlValidators[
                    key as keyof GoApplicationFormControlValidatorsMap
                ];

            if (
                this.goApplicationTypeToControlsMapping[
                    goApplicationType
                ].includes(key as keyof GoApplicationRequestFormModel)
            ) {
                control.setValidators(validators);

                const defaultValue =
                    this.controlDefaultValues[
                        key as keyof GoApplicationRequest
                    ];

                if (
                    control.value === null &&
                    defaultValue &&
                    control.disabled === false
                ) {
                    control.setValue(defaultValue, { emitEvent: false });
                }
            } else {
                control.clearValidators();
            }

            control.updateValueAndValidity({ emitEvent: false });
        });
    }

    /**
     * Returns the form for creating a go application.
     * @returns The go application request form.
     */
    getGoApplicationRequestForm(): FormGroup<
        FormGroupType<GoApplicationRequestFormModel>
    > {
        return this.applicationPreparationForm;
    }

    getValue(
        form: FormGroup<FormGroupType<GoApplicationRequestFormModel>>,
    ): GoApplicationRequest {
        return {
            ...form.getRawValue(),
            heightToTip: tryParseToNumber(form.controls.heightToTip.value),
            fixedErp: tryParseToNumber(form.controls.fixedErp.value),
            mobileErp: tryParseToNumber(form.controls.mobileErp.value),
            latitude: tryParseToNumber(form.controls.latitude.value),
            longitude: tryParseToNumber(form.controls.longitude.value),
            towerRegistrationNumber: tryParseToNumber(
                form.controls.towerRegistrationNumber.value,
            ),
        };
    }

    patchValue(
        form: FormGroup<FormGroupType<GoApplicationRequestFormModel>>,
        value: GoApplicationRequest,
    ): void {
        form.patchValue({
            ...value,
            heightToTip: value.heightToTip?.toString() ?? null,
            fixedErp: value.fixedErp?.toString() ?? null,
            mobileErp: value.mobileErp?.toString() ?? null,
            latitude: value.latitude?.toString() ?? null,
            longitude: value.longitude?.toString() ?? null,
            towerRegistrationNumber:
                value.towerRegistrationNumber?.toString() ?? null,
        });
    }

    /**
     * Updates the go application request form with the filled out information from the individual step forms.
     * @param request
     */
    patchGoApplicationRequestForm(
        value: Partial<GoApplicationRequestFormModel>,
    ): void {
        this.applicationPreparationForm.patchValue(value);
    }

    isExclusiveStationClassOptionInvalid(
        locationTypeControl: FormControl<GoLocationType | null>,
        stationClassTypeControl: FormControl<GoStationClassType | null>,
    ): boolean {
        return (
            this.isExclusiveStationClassOptionDisabled(locationTypeControl) &&
            stationClassTypeControl.value === GoStationClassType.Exclusive
        );
    }

    isExclusiveStationClassOptionDisabled(
        locationTypeControl: FormControl<GoLocationType | null>,
    ): boolean {
        return locationTypeControl.value === GoLocationType.MobileOnly;
    }

    isDataDirectionTypeControlRequired(
        locationTypeControl: FormControl<GoLocationType | null>,
        stationClassTypeControl: FormControl<GoStationClassType | null>,
    ): boolean {
        return (
            locationTypeControl.value === GoLocationType.Fixed &&
            stationClassTypeControl.value === GoStationClassType.Shared
        );
    }

    isHeightToTipControlRequired(
        locationTypeControl: FormControl<GoLocationType | null>,
    ): boolean {
        return locationTypeControl.value === GoLocationType.Fixed;
    }

    isHeightToTipControlDisabled(
        locationTypeControl: FormControl<GoLocationType | null>,
    ): boolean {
        return this.isHeightToTipDisabled(locationTypeControl.value);
    }

    isHeightToTipDisabled(locationType: GoLocationType | null): boolean {
        return locationType === GoLocationType.MobileOnly;
    }

    isFixedErpControlRequired(
        locationTypeControl: FormControl<GoLocationType | null>,
    ): boolean {
        return locationTypeControl.value === GoLocationType.Fixed;
    }

    isFixedErpControlDisabled(
        locationTypeControl: FormControl<GoLocationType | null>,
    ): boolean {
        return locationTypeControl.value === GoLocationType.MobileOnly;
    }

    /**
     * Updates the station class section controls based on the location type.
     * @param locationTypeControl The location type control.
     * @param stationClassTypeControl The station class type control.
     */
    updateStationClassSectionControls(
        locationTypeControl: FormControl<GoLocationType | null>,
        stationClassTypeControl: FormControl<GoStationClassType | null>,
    ): void {
        if (this.isExclusiveStationClassOptionDisabled(locationTypeControl)) {
            stationClassTypeControl.patchValue(GoStationClassType.Shared, {
                emitEvent: false,
            });
        }

        stationClassTypeControl.updateValueAndValidity();
    }

    /**
     * Updates the antenna height section controls based on the location type.
     * @param locationTypeControl The location type control.
     * @param antennaHeightUnitTypeControl The antenna height unit type control.
     * @param heightToTipControl The height to tip control.
     */
    updateAntennaHeightSectionControls(
        locationTypeControl: FormControl<GoLocationType | null>,
        antennaHeightUnitTypeControl: FormControl<GoAntennaHeightUnitType | null>,
        heightToTipControl: FormControl<string | null>,
    ): void {
        if (this.isHeightToTipControlRequired(locationTypeControl)) {
            antennaHeightUnitTypeControl.enable();
            heightToTipControl.enable();
        } else {
            antennaHeightUnitTypeControl.setValue(null, {
                emitEvent: false,
            });
            antennaHeightUnitTypeControl.disable();
            heightToTipControl.setValue(null);
            heightToTipControl.disable();
        }

        antennaHeightUnitTypeControl.updateValueAndValidity();
        heightToTipControl.updateValueAndValidity();
    }

    /**
     * Updates the minimum ERP section controls based on the location type.
     * @param locationTypeControl The location type control.
     * @param fixedErpControl The fixed ERP control.
     */
    updateMinimumErpSectionControls(
        locationTypeControl: FormControl<GoLocationType | null>,
        fixedErpControl: FormControl<string | null>,
    ): void {
        if (this.isFixedErpControlRequired(locationTypeControl)) {
            fixedErpControl.enable();
        } else {
            fixedErpControl.setValue(null);
            fixedErpControl.disable();
        }

        fixedErpControl.updateValueAndValidity();
    }

    /**
     * Updates the data direction type section controls based on the location type and station class type.
     * @param locationTypeControl The location type control.
     * @param stationClassTypeControl The station class type control.
     * @param dataDirectionTypeControl The data direction type control.
     */
    updateDataDirectionTypeSectionControls(
        locationTypeControl: FormControl<GoLocationType | null>,
        stationClassTypeControl: FormControl<GoStationClassType | null>,
        dataDirectionTypeControl: FormControl<GoDataDirectionType | null>,
    ): void {
        if (
            this.isDataDirectionTypeControlRequired(
                locationTypeControl,
                stationClassTypeControl,
            )
        ) {
            dataDirectionTypeControl.enable();
        } else {
            dataDirectionTypeControl.setValue(null);
            dataDirectionTypeControl.disable();
        }
    }

    /**
     * Validates the station class type control based on the location type and ensures that the exclusive station class type is not selected when the location type is mobile only.
     * @returns A validation error object if the control is invalid, or null if it is valid.
     */
    private stationClassTypeControlValidator(): ValidatorFn {
        return (
            stationClassTypeAbstractControl: AbstractControl,
        ): ValidationErrors | null => {
            const stationClassTypeControl =
                stationClassTypeAbstractControl as FormControl<GoStationClassType | null>;

            const goApplicationRequestFormModelFormGroup =
                stationClassTypeControl.parent as FormGroup<
                    FormGroupType<GoApplicationRequestFormModel>
                >;

            if (goApplicationRequestFormModelFormGroup === null) {
                return null;
            }

            const locationTypeControl =
                goApplicationRequestFormModelFormGroup?.controls.locationType;

            if (locationTypeControl === null) {
                return null;
            }

            if (
                this.isExclusiveStationClassOptionInvalid(
                    locationTypeControl,
                    stationClassTypeControl,
                )
            ) {
                return {
                    invalid:
                        GoParametersValidationMessages.exclusiveStationClassTypeInvalid,
                };
            }

            return null;
        };
    }

    /**
     * Validates the data direction type control based on the location type and station class type, ensuring that it is required when the conditions are met.
     * @returns A validation error object if the control is invalid, or null if it is valid.
     */
    private dataDirectionTypeControlRequiredValidator(): ValidatorFn {
        return (
            dataDirectionTypeAbstractControl: AbstractControl,
        ): ValidationErrors | null => {
            const dataDirectionTypeControl =
                dataDirectionTypeAbstractControl as FormControl<GoDataDirectionType | null>;

            const goApplicationRequestFormModelFormGroup =
                dataDirectionTypeControl.parent as FormGroup<
                    FormGroupType<GoApplicationRequestFormModel>
                >;

            if (goApplicationRequestFormModelFormGroup === null) {
                return null;
            }

            const locationTypeControl =
                goApplicationRequestFormModelFormGroup?.controls.locationType;
            const stationClassTypeControl =
                goApplicationRequestFormModelFormGroup?.controls
                    .stationClassType;

            if (
                locationTypeControl === null ||
                stationClassTypeControl === null
            ) {
                return null;
            }

            if (
                dataDirectionTypeControl.value === null &&
                this.isDataDirectionTypeControlRequired(
                    locationTypeControl,
                    stationClassTypeControl,
                )
            ) {
                return {
                    required:
                        GoParametersValidationMessages.dataDirectionTypeRequired,
                };
            }

            return null;
        };
    }

    /**
     * Validates the height to tip control based on the location type, ensuring that it is required when the conditions are met.
     * @returns A validation error object if the control is invalid, or null if it is valid.
     */
    private heightToTipControlRequiredValidator(): ValidatorFn {
        return (
            heightToTipAbstractControl: AbstractControl,
        ): ValidationErrors | null => {
            const heightToTipControl =
                heightToTipAbstractControl as FormControl<string | null>;

            const goApplicationRequestFormModelFormGroup =
                heightToTipControl.parent as FormGroup<
                    FormGroupType<GoApplicationRequestFormModel>
                >;

            if (goApplicationRequestFormModelFormGroup === null) {
                return null;
            }

            const locationTypeControl =
                goApplicationRequestFormModelFormGroup?.controls.locationType;

            if (locationTypeControl === null) {
                return null;
            }

            if (
                this.isHeightToTipControlRequired(locationTypeControl) &&
                isFalsyOrWhitespace(heightToTipControl.value)
            ) {
                return {
                    required:
                        GoParametersValidationMessages.heightToTipRequired,
                };
            }

            return null;
        };
    }

    /**
     * Validates the fixed ERP control based on the location type, ensuring that it is required when the conditions are met.
     * @returns A validation error object if the control is invalid, or null if it is valid.
     */
    private fixedErpControlRequiredValidator(): ValidatorFn {
        return (
            fixedErpAbstractControl: AbstractControl,
        ): ValidationErrors | null => {
            const fixedErpControl = fixedErpAbstractControl as FormControl<
                string | null
            >;

            const goApplicationRequestFormModelFormGroup =
                fixedErpControl.parent as FormGroup<
                    FormGroupType<GoApplicationRequestFormModel>
                >;

            if (goApplicationRequestFormModelFormGroup === null) {
                return null;
            }

            const locationTypeControl =
                goApplicationRequestFormModelFormGroup?.controls.locationType;

            if (locationTypeControl === null) {
                return null;
            }

            if (
                this.isFixedErpControlRequired(locationTypeControl) &&
                isFalsyOrWhitespace(fixedErpControl.value)
            ) {
                return {
                    required: GoParametersValidationMessages.fixedErpRequired,
                };
            }

            return null;
        };
    }

    private toggleAsrControlValidator(): ValidatorFn {
        return (
            coordinatesTypeControl: AbstractControl,
        ): ValidationErrors | null => {
            const formGroup = coordinatesTypeControl.parent as FormGroup<
                FormGroupType<GoApplicationRequestFormModel>
            >;
            if (formGroup === null) {
                return null;
            }

            const asrValueControl = formGroup?.controls.towerRegistrationNumber;
            if (asrValueControl === null) {
                return null;
            }

            if (coordinatesTypeControl.value === GoCoordinatesType.ASR) {
                asrValueControl.setValidators([Validators.required]);
                asrValueControl.updateValueAndValidity();
            } else {
                asrValueControl.clearValidators();
                asrValueControl.updateValueAndValidity();
            }

            return null;
        };
    }
}
